import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';
import PartnershipForm from '../components/forms/PartnershipForm';

const PartnershipFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    partnerName: '',
    type: [],
    country: '',
    description: '',
    details: '',
    website: '',
    logoUrl: '',
    logoFile: null,
    isActive: true,
    startDate: new Date().toISOString().split('T')[0],
    removeLogo: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [originalLogoUrl, setOriginalLogoUrl] = useState('');

  // Load partnership data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadPartnership = async () => {
        try {
          const partnership = await adminAPI.getPartnershipById(id);
          const logoUrl = partnership.logo || partnership.logoUrl || '';
          const formattedData = {
            ...partnership,
            type: Array.isArray(partnership.type)
              ? partnership.type
              : partnership.type
                ? [partnership.type]
                : [],
            logoUrl: logoUrl,
            logoFile: null,
            startDate: partnership.startDate ? partnership.startDate.split('T')[0] : '',
            removeLogo: false
          };
          setFormData(formattedData);
          setOriginalLogoUrl(logoUrl);
        } catch (error) {
          console.error('Error loading partnership:', error);
          toast.error('Failed to load partnership data');
          navigate('/admin/dashboard');
        }
      };
      loadPartnership();
    }
  }, [id, isEditMode, navigate]);

  const handleSubmit = async (formData) => {
    setSubmitting(true);

    try {
      // Determine if logo was removed
      const logoWasRemoved = isEditMode && originalLogoUrl && !formData.logoUrl && !formData.logoFile;

      // ALWAYS use FormData (consistent with backend expectations)
      const formDataToSend = new FormData();

      // Add file if exists
      if (formData.logoFile) {
        formDataToSend.append('file', formData.logoFile);
      }

      // Add remove_logo flag if logo was removed
      if (logoWasRemoved) {
        formDataToSend.append('remove_logo', 'true');
      }

      // Add all required fields
      formDataToSend.append('partner_name', formData.partnerName);
      if (Array.isArray(formData.type)) {
        formData.type.forEach(t => {
          formDataToSend.append('type', t);
        });
      } else if (formData.type) {
        formDataToSend.append('type', formData.type);
      }
      formDataToSend.append('country', formData.country);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('details', formData.details);

      // Add optional fields
      if (formData.website && formData.website.trim()) {
        formDataToSend.append('website', formData.website);
      }

      if (isEditMode) {
        await adminAPI.updatePartnership(id, formDataToSend);
        toast.success('Partnership updated successfully');
      } else {
        await adminAPI.createPartnership(formDataToSend);
        toast.success('Partnership created successfully');
      }

      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving partnership:', error);

      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to save partnership';

      toast.error(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div className="container mx-auto p-6">
      <PartnershipForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitting={submitting}
        isEdit={isEditMode}
      />
    </div>
  );
};

export default PartnershipFormPage;